(function () {
  const MIN_WORD_LENGTH = 4;
  const STOP_WORDS = new Set([
    'a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'for', 'from', 'has', 'he',
    'in', 'is', 'it', 'its', 'of', 'on', 'or', 'that', 'the', 'to', 'was', 'will',
    'with', 'you', 'your', 'my', 'our', 'all', 'any', 'can', 'get', 'help', 'find'
  ]);

  function loadDictionary() {
    const node = document.getElementById('search-typo-dictionary');
    if (!node) return new Set();

    try {
      const words = JSON.parse(node.textContent);
      return new Set(words.filter((word) => word && word.length >= 2));
    } catch {
      return new Set();
    }
  }

  function levenshtein(a, b) {
    if (a === b) return 0;
    if (!a.length) return b.length;
    if (!b.length) return a.length;

    const matrix = Array.from({ length: a.length + 1 }, (_, i) => [i]);

    for (let j = 1; j <= b.length; j += 1) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= a.length; i += 1) {
      for (let j = 1; j <= b.length; j += 1) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j - 1] + cost
        );
      }
    }

    return matrix[a.length][b.length];
  }

  function hasAdjacentTransposition(a, b) {
    if (a.length !== b.length || a.length < 2) return false;

    let mismatches = 0;

    for (let i = 0; i < a.length; i += 1) {
      if (a[i] === b[i]) continue;
      mismatches += 1;
      if (mismatches > 1) return false;
      if (a[i] === b[i + 1] && a[i + 1] === b[i]) return true;
    }

    return false;
  }

  function normalizeWord(word) {
    return word.toLowerCase().replace(/[^a-z0-9']/g, '');
  }

  function preserveCase(original, replacement) {
    if (original === original.toUpperCase()) return replacement.toUpperCase();
    if (original[0] === original[0].toUpperCase()) {
      return replacement.charAt(0).toUpperCase() + replacement.slice(1);
    }
    return replacement;
  }

  function matchesTypoTolerance(query, target) {
    if (query === target) return false;

    if (query.length >= MIN_WORD_LENGTH && target.length >= MIN_WORD_LENGTH) {
      if (levenshtein(query.slice(0, MIN_WORD_LENGTH), target.slice(0, MIN_WORD_LENGTH)) > 1) {
        return false;
      }
    }

    const distance = levenshtein(query, target);
    if (distance === 1) return true;
    if (distance === 2 && hasAdjacentTransposition(query, target)) return true;
    if (query.length >= 6 && target.length >= 6 && distance === 2) return true;

    return false;
  }

  function isValidWordForm(normalized, dictionary) {
    if (!normalized) return false;
    if (dictionary.has(normalized)) return true;
    if (STOP_WORDS.has(normalized)) return true;

    if (normalized.endsWith("'s")) {
      const base = normalized.slice(0, -2);
      if (dictionary.has(base) || dictionary.has(`${base}s`)) return true;
    }

    if (normalized.endsWith('s') && normalized.length > 3) {
      const singular = normalized.slice(0, -1);
      if (dictionary.has(singular)) return true;
    }

    if (normalized.endsWith('es') && normalized.length > 4) {
      const singular = normalized.slice(0, -2);
      if (dictionary.has(singular)) return true;
    }

    return false;
  }

  function isPluralOrPossessiveOf(normalized, term) {
    return normalized === `${term}s` || normalized === `${term}es` || normalized === `${term}'s`;
  }

  function getSuggestions(word, dictionary) {
    const normalized = normalizeWord(word);
    if (normalized.length < 3) return [];
    if (isValidWordForm(normalized, dictionary)) return [];

    const suggestions = new Map();

    for (const term of dictionary) {
      if (term.length < 3 || term === normalized) continue;
      if (isPluralOrPossessiveOf(normalized, term)) continue;

      if (term.startsWith(normalized) && term.length > normalized.length) {
        suggestions.set(term, term.length - normalized.length);
        continue;
      }

      if (matchesTypoTolerance(normalized, term)) {
        suggestions.set(term, levenshtein(normalized, term));
      }
    }

    return [...suggestions.entries()]
      .sort((a, b) => a[1] - b[1] || a[0].length - b[0].length)
      .slice(0, 5)
      .map(([term]) => term);
  }

  function isMisspelling(word, dictionary) {
    return getSuggestions(word, dictionary).length > 0;
  }

  function escapeHtml(value) {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function buildMirrorHtml(text, dictionary) {
    if (!text) return '';

    const wordPattern = /[^\s]+/g;
    let html = '';
    let lastIndex = 0;
    let match;

    while ((match = wordPattern.exec(text)) !== null) {
      html += escapeHtml(text.slice(lastIndex, match.index));

      const word = match[0];
      if (isMisspelling(word, dictionary)) {
        html += `<span class="search-typo-word">${escapeHtml(word)}</span>`;
      } else {
        html += escapeHtml(word);
      }

      lastIndex = match.index + word.length;
    }

    html += escapeHtml(text.slice(lastIndex));
    return html;
  }

  function getWordAtCaret(input) {
    const text = input.value;
    const index = input.selectionStart ?? text.length;
    if (!text) return null;

    let start = index;
    let end = index;

    while (start > 0 && !/\s/.test(text[start - 1])) start -= 1;
    while (end < text.length && !/\s/.test(text[end])) end += 1;

    const word = text.slice(start, end);
    if (!word) return null;

    return { text: word, start, end };
  }

  function initSearchTypoHighlight(root, dictionary) {
    const input = root.querySelector('.search-typo-field > input[name="q"]');
    const mirror = root.querySelector('.search-typo-field__mirror');
    const suggestions = root.querySelector('.search-typo-suggestions');
    if (!input || !mirror || !suggestions) return;

    let activeWord = null;
    let activeWordRange = null;

    const hideSuggestions = () => {
      suggestions.hidden = true;
      suggestions.innerHTML = '';
      activeWord = null;
      activeWordRange = null;
      input.removeAttribute('aria-expanded');
    };

    const showSuggestions = (wordInfo) => {
      const options = getSuggestions(wordInfo.text, dictionary);
      if (!options.length) {
        hideSuggestions();
        return;
      }

      activeWord = wordInfo.text;
      activeWordRange = { start: wordInfo.start, end: wordInfo.end };
      suggestions.innerHTML = options
        .map(
          (option) =>
            `<button type="button" class="search-typo-suggestions__option" role="option" data-replacement="${escapeHtml(option)}">${escapeHtml(option)}</button>`
        )
        .join('');
      suggestions.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    };

    const render = () => {
      mirror.innerHTML = buildMirrorHtml(input.value, dictionary);
      mirror.scrollLeft = input.scrollLeft;

      if (activeWord && !getSuggestions(activeWord, dictionary).length) {
        hideSuggestions();
      }
    };

    input.addEventListener('click', () => {
      window.requestAnimationFrame(() => {
        const wordInfo = getWordAtCaret(input);
        if (wordInfo && isMisspelling(wordInfo.text, dictionary)) {
          if (activeWord === wordInfo.text && !suggestions.hidden) {
            hideSuggestions();
            return;
          }
          showSuggestions(wordInfo);
          return;
        }

        hideSuggestions();
      });
    });

    suggestions.addEventListener('click', (event) => {
      const option = event.target.closest('.search-typo-suggestions__option');
      if (!option || !activeWord || !activeWordRange) return;

      event.preventDefault();

      const replacement = option.dataset.replacement;
      if (!replacement) return;

      const original = input.value.slice(activeWordRange.start, activeWordRange.end);
      input.value =
        input.value.slice(0, activeWordRange.start) +
        preserveCase(original, replacement) +
        input.value.slice(activeWordRange.end);
      hideSuggestions();
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.focus();
      render();
    });

    input.addEventListener('input', () => {
      if (activeWord && !input.value.includes(activeWord)) {
        hideSuggestions();
      }
      render();
    });

    input.addEventListener('scroll', render);
    input.addEventListener('focus', render);
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') hideSuggestions();
    });

    root.closest('form')?.addEventListener('reset', () => {
      window.requestAnimationFrame(() => {
        hideSuggestions();
        render();
      });
    });

    document.addEventListener('click', (event) => {
      if (root.contains(event.target)) return;
      hideSuggestions();
    });

    render();
  }

  function loadFeaturedBundles() {
    const node = document.getElementById('search-featured-bundles');
    if (!node) return new Map();

    try {
      const bundles = JSON.parse(node.textContent);
      return new Map(bundles.map((bundle) => [bundle.handle, bundle]));
    } catch {
      return new Map();
    }
  }

  function productHandleFromLink(link) {
    const match = (link.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  }

  function fillFeaturedBundle(card, bundle) {
    card.href = bundle.url;
    const image = card.querySelector('img');
    if (image) {
      if (bundle.image) {
        image.src = bundle.image;
        image.alt = bundle.title;
      } else {
        image.remove();
      }
    }

    card.querySelector('.search-featured-bundle__title').textContent = bundle.title;
    card.querySelector('.search-featured-bundle__price').textContent = bundle.price;

    const badge = card.querySelector('.search-featured-bundle__badge');
    const compare = card.querySelector('.search-featured-bundle__compare');
    const save = card.querySelector('.search-featured-bundle__save');
    if (bundle.percent > 0) {
      badge.hidden = false;
      badge.textContent = `Bundle & save ${bundle.percent}%`;
      compare.hidden = false;
      compare.textContent = bundle.compare;
      save.hidden = false;
      save.textContent = `Save ${bundle.save}`;
    }
  }

  function mountFeaturedBundle(drawer, bundles) {
    const host = drawer.querySelector('predictive-search');
    const template = drawer.querySelector('#search-featured-bundle-template');
    if (!host || !template || bundles.size === 0 || host.dataset.featuredBundleBound === 'true') return;

    host.dataset.featuredBundleBound = 'true';

    const apply = () => {
      const results = host.querySelector('[slot="results"]');
      if (!results) return;

      results.querySelectorAll('.search-featured-bundle').forEach((node) => node.remove());
      if (!template.content.firstElementChild) return;

      const productList = Array.from(results.querySelectorAll('.predictive-search__tab-content')).find((node) => (
        node.querySelector('a.predictive-search-result[href*="/products/"]')
      ));
      if (!productList) return;

      const ranked = Array.from(productList.querySelectorAll('a.predictive-search-result[href*="/products/"]'));
      const match = ranked.find((link) => bundles.has(productHandleFromLink(link)));
      if (!match) return;

      const bundle = bundles.get(productHandleFromLink(match));
      const card = template.content.firstElementChild.cloneNode(true);
      fillFeaturedBundle(card, bundle);
      productList.prepend(card);
      ranked.forEach((link) => {
        if (productHandleFromLink(link) === bundle.handle) link.remove();
      });
    };

    new MutationObserver(apply).observe(host, { childList: true });
  }

  function boot() {
    const dictionary = loadDictionary();
    const bundles = loadFeaturedBundles();
    document.querySelectorAll('#search-drawer').forEach((drawer) => {
      drawer.querySelectorAll('.search-drawer__input').forEach((root) => {
        initSearchTypoHighlight(root, dictionary);
      });
      mountFeaturedBundle(drawer, bundles);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
